<template lang="pug">
  #challenge-page
    WtFullPageLoader(v-if="_.isEmpty(getChallengeById(challengeId))")
    template(v-else)
      WellnessChallenge(v-if="getChallengeById(challengeId).isWellness")
      ChartChallenge(v-else-if="getChallengeById(challengeId).isChartChallenge")
      PreventativeChallenge(v-else-if="getChallengeById(challengeId).isPreventative")
      MapChallenge(v-else)

</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { fetchAllMixin } from "@/mixins/fetchAllMixin";
import WtFullPageLoader from "@@/WtFullPageLoader/WtFullPageLoader";
import MapChallenge from "@@/Challenge/MapChallenge/MapChallenge";
import WellnessChallenge from "@@/Challenge/WellnessChallenge/WellnessChallenge";
import ChartChallenge from "@@/Challenge/ChartChallenge/ChartChallenge";
import PreventativeChallenge from "@@/Challenge/PreventativeChallenge/PreventativeChallenge";

export default {
  name: "Challenge",
  components: {
    WtFullPageLoader,
    WellnessChallenge,
    MapChallenge,
    ChartChallenge,
    PreventativeChallenge
  },

  mixins: [fetchAllMixin],

  data() {
    return {
      challengeLoaded: false,
      reloadPage: false
    };
  },

  computed: {
    ...mapGetters("pages", ["isPageLoaded", "isPageLoading"]),
    ...mapGetters("challenges", [
      "getDiscussionById",
      "discussionLoaded",
      "getLeaderboardById",
      "leaderboardLoaded",
      "getAggregateById",
      "aggregateLoaded",
      "joining",
      "getChallengeById"
    ]),
    ...mapGetters("members", [
      "getMilestonesById",
      "milestonesLoaded",
      "challenges",
      "memberTeamsLoaded"
    ]),
    ...mapGetters("program", ["coreProgramMeta", "programTeamsLoaded"]),

    page() {
      return `Challenge${this.challengeId}`;
    },

    challengeId() {
      return this.$route.params.challengeId;
    },

    challenge() {
      return this.getChallengeById(this.challengeId);
    },

    fullPageLoader() {
      // since there is no intial state for 'isPageLoading(this.page)'
      if (this._.isUndefined(this.isPageLoading(this.page))) {
        return true;
      }
      return this.isPageLoading(this.page);
    }
  },

  watch: {
    async challengeId() {
      this.challengeLoaded = false;
      await this.toFetchOrNotToFetch(this, this.page);
      this.challengeLoaded = true;
    }
  },

  beforeRouteEnter(to, _, next) {
    next(async vm => {
      vm.unsubscribeAddChallenge = vm.$store.subscribe(async mutation => {
        if (mutation.type === "members/ADD_CHALLENGE") {
          vm.reloadPage = true;
          await vm.toFetchOrNotToFetch(vm, vm.page, {
            force: vm.reloadPage
          });
          vm.reloadPage = false;
        }
      });

      if (!vm.joining) {
        await vm.toFetchOrNotToFetch(vm, vm.page);
        // upon revisit to challenge page - make sure discussion pagination is at page 1
        if (
          vm._.get(
            vm.getDiscussionById(to.params.challengeId),
            "pagination.currentPage",
            null
          ) !== 1 &&
          !vm.isPageLoading(vm.page) // might be loading if ThePagesLoader is working
        ) {
          vm.fetchChallengeDiscussion({ challengeId: to.params.challengeId });
        }
        // check for invite code from email
        const inviteCode = to.query.invite;
        if (inviteCode) {
          const challenge = vm.getChallengeById(to.params.challengeId);
          try {
            await vm.join({ challenge, inviteCode });
          } catch (error) {
            console.log(error);
          }
        }
      }
    });
  },

  beforeDestroy() {
    this.unsubscribeAddChallenge();
  },

  methods: {
    ...mapActions("pages", ["setPageLoaded", "setPageLoading"]),
    ...mapActions("challenges", [
      "fetchChallenge",
      "join",
      "fetchChallengeDiscussion",
      "fetchLeaderboard",
      "fetchChallengeAggregate"
    ]),
    ...mapActions("members", ["fetchMilestones", "fetchMemberTeams"]),
    ...mapActions("program", ["fetchProgramTeams"]),
    ...mapActions(["snackBar"]),

    // pass as prop to challenge components
    fetchDiscussion(photosOnly = false, page = null) {
      return this.fetchChallengeDiscussion({
        challengeId: this.challengeId,
        photosOnly,
        page
      });
    },

    async fetchAllPage(vm) {
      const challenge = await vm.fetchChallenge(vm.challengeId);

      if (challenge.between === "groups") {
        if (!vm.memberTeamsLoaded) {
          await vm.fetchMemberTeams(vm.$auth.user().id);
        }
        if (!vm.programTeamsLoaded) {
          await vm.fetchProgramTeams();
        }
      }

      if (!this.aggregateLoaded) {
        await vm.fetchChallengeAggregate(challenge.id);
      }

      await vm.fetchLeaderboard({
        challengeId: challenge.id,
        perPage: 15
      });

      // wellness challenges don't have milestones (yet)
      if (!challenge.isWellness) {
        await vm.fetchMilestones({
          memberId: vm.$auth.user().id,
          challengeId: challenge.id
        });
      }

      await vm.fetchChallengeDiscussion({ challengeId: challenge.id });
    }
  }
};
</script>

<style lang="sass" scoped></style>
